<template lang="pug">
.partially-cancel-form
    .partial-cancel-context
        el-form-item.context-field(:label="$t('Type')")
            el-radio-group(v-model="form.type")
                el-radio-button(label="cancellation") {{ $t('Cancellation') }}
                el-radio-button(label="return") {{ $t('Return') }}
        el-form-item.context-field(v-if="form.type === 'return'" :label="$t('Status')")
            el-select(v-model="form.status" style="min-width: 140px")
                el-option(value="pending" :label="$t('Pending')")
                el-option(value="approved" :label="$t('Approved')")

    .purchased-items(
        ref="container"
        @scroll="handleScroll"
        v-dragscroll
    )
        table.default-table(v-if="purchase && pagination.items.length")
            thead
                tr
                    th.min.checkbox
                        transition(name="fade")
                            i.el-icon-fa-arrow-down.fas(v-if="nothingSelected")
                    //- th.min.media
                    th.col-left.status
                    th.col-left.item {{ $t('Item') }}
                    th.col-left {{ $t('Properties') }}
                    th.dimmed.col-right {{ $t('Price') }}
                    th.col-center {{ $t('Quantity') }}
                    th.col-right {{ $t('Total') }}
            tbody
                tr.spacer
                    td(colspan="7")
                template(v-for="item in pagination.items")
                    tr.item(:key="`item-${item.id}`" :class="{ 'item--excluded': isExcluded(item) }")
                        td.min.checkbox
                            el-tooltip(
                                v-if="isExcluded(item)"
                                :content="$t('Excluded from returns per § 312g BGB')"
                                placement="top"
                                effect="dark"
                            )
                                el-checkbox(:value="false" disabled)
                            el-checkbox(
                                v-else
                                :value="item.selected"
                                @change="checked => toggleItem(item, checked)"
                            )
                        //- td.min.media
                        //-     //- NOTE: neeeds article/product instance
                        //-     item-media-preview(:item="item")
                        td.col-left.status
                            template(v-if="item.status && item.status.key === 'canceled'")
                                i.el-icon-fa-exclamation-triangle.fas
                            template(v-else-if="isExcluded(item)")
                                el-tooltip(
                                    :content="$t('Excluded from returns per § 312g BGB')"
                                    placement="top"
                                    effect="dark"
                                )
                                    i.el-icon-fa-ban.fas.excluded-icon
                        td.col-left.item
                            .name(v-line-clamp="1")
                                b {{ item.name }}
                            small {{ item.product_nr }}
                            span &nbsp;/&nbsp;
                            small {{ item.sku }}
                        td.col-left
                            .properties
                                property-value(
                                    v-for="property in item.selectedProperties"
                                    :key="property.id"
                                    :property="property"
                                )
                        td.min.dimmed.col-right
                            .sale-price(v-if="item.sale_price && item.sale_price < item.price")
                                .nobr
                                    s.strike-through.dimmed {{ item.price | moneyFormat(purchase.currency) }}
                                    //- span.percentage {{ (item.price - item.sale_price) / item.price * 100 | round(2) }}%
                                nobr.red.dimmed {{ item.sale_price | moneyFormat(purchase.currency) }}
                            nobr.dimmed(v-else) {{ item.price | moneyFormat(purchase.currency) }}
                        td.min.col-center
                            template(v-if="item.selected === true")
                                el-input-number.qty(
                                    :value="item.take_back_qty"
                                    size="small"
                                    :min="1"
                                    :max="item.qty"
                                    :step-strictly="true"
                                    @change="qty => updateItemTakeBackQty(item, qty)"
                                )
                            template(v-else)
                                el-input-number.qty(
                                    :value="item.qty"
                                    size="small"
                                    disabled
                                )
                        td.min.col-right
                            template(v-if="item.selected === true")
                                .sale-price(v-if="item.sale_price && item.sale_price < item.price")
                                    .nobr
                                        //- s.strike-through {{ item.price * item.take_back_qty | moneyFormat(purchase.currency) }}
                                        small.percentage {{ (item.price - item.sale_price) / item.price * 100 | round(2) }}%
                                    b.nobr.red {{ item.sale_price * item.take_back_qty | moneyFormat(purchase.currency) }}
                                b.nobr(v-else) {{ item.price * item.take_back_qty | moneyFormat(purchase.currency) }}
                            template(v-else)
                                .sale-price(v-if="item.sale_price && item.sale_price < item.price")
                                    .nobr
                                        //- s.strike-through {{ item.price * item.qty | moneyFormat(purchase.currency) }}
                                        small.percentage {{ (item.price - item.sale_price) / item.price * 100 | round(2) }}%
                                    b.nobr.red {{ item.sale_price * item.qty | moneyFormat(purchase.currency) }}
                                b.nobr(v-else) {{ item.price * item.qty | moneyFormat(purchase.currency) }}

                    //- Return-specific per-item meta (reason + comment) lives
                    //- in a sub-row that spans the whole table width so the
                    //- main item row stays scannable. Only shown when the
                    //- item is selected AND the row is a return. Sibling of
                    //- tr.item — wrapped in template(v-for) so both rows
                    //- render at tbody level.
                    tr.return-meta-row(
                        v-if="item.selected === true && form.type === 'return'"
                        :key="`meta-${item.id}`"
                    )
                        td(colspan="7")
                            .return-meta
                                el-select.reason(
                                    v-model="item.return_reason_id"
                                    size="mini"
                                    clearable
                                    :placeholder="returnReasons.length ? $t('Reason') : $t('No reasons configured yet')"
                                    :disabled="!returnReasons.length"
                                    @change="updateItems"
                                )
                                    el-option(
                                        v-for="reason in returnReasons"
                                        :key="reason.id"
                                        :value="reason.id"
                                        :label="reason.name"
                                    )
                                el-input.comment(
                                    v-model="item.return_comment"
                                    size="mini"
                                    :placeholder="$t('Comment (optional)')"
                                    @input="updateItems"
                                )
                                a.configure-hint(
                                    v-if="!returnReasons.length"
                                    :href="`/settings/${activeCmsId}/shop#return-reasons-anchor`"
                                    target="_blank"
                                ) {{ $t('Configure reasons') }} →
                tr.spacer
                    td(colspan="7")
                        .drawer-spinner-container(v-if="loading")
                            .spinner-background
                                .spinner

    //- Pending returns are provisional — the FINAL refund amount is
    //- captured in the approval drawer once goods are reviewed. Hiding
    //- the editable amount fields here removes the redundant ask. For
    //- direct-entry cases (cancellation, OR a return created already
    //- approved/rejected) there's no later approval step, so the fields
    //- stay visible and capture the final amount.
    template(v-if="!isPendingReturn")
        .flex-row
            el-form-item.grow(
                v-if="form.items.length"
                :label="$t('Net amount to be credited')"
                prop="credited_amount_net"
            )
                el-input(
                    type="number"
                    v-model="creditedAmountNet"
                    step="0.01"
                    min="0"
                )
                    template(slot="append")
                        i.el-icon-fa-euro-sign.fas(v-if="currency === '€'")
                        i.el-icon-fa-dollar-sign.fas(v-else-if="currency === '$'")
                        b.currency(v-else) {{ currency }}
            el-form-item(
                v-else
                :label="$t('Tax rate')"
            )
                el-select.is-full-width(
                    placeholder="" v-model="taxRate" valueKey="id"
                    :disabled="taxOptions.length <= 1"
                    :class="{ 'loading': options.tax.loading }"
                )
                    el-option(
                        v-for="(item, key) in taxOptions"
                        :key="item.id"
                        :label="item.label || item.name || `${item.value} %`"
                        :value="item"
                    )
            el-form-item.grow(:label="$t('Gross amount to be credited')" prop="credited_amount")
                el-input(
                    type="number"
                    v-model="creditedAmount"
                    step="0.01"
                    min="0"
                )
                    template(slot="append")
                        i.el-icon-fa-euro-sign.fas(v-if="currency === '€'")
                        i.el-icon-fa-dollar-sign.fas(v-else-if="currency === '$'")
                        b.currency(v-else) {{ currency }}
    //- Hint shown in place of the amount inputs for pending returns.
    p.pending-amount-hint(v-else)
        i.el-icon-fa-circle-info.fas
        | {{ $t('The refund amount will be confirmed when you approve the return.') }}
    el-form-item(:label="$t('Comment')" prop="comment")
        el-input(
            type="textarea"
            :autosize="{ minRows: 5, maxRows: 40 }"
            v-model="form.comment"
        )
</template>

<i18n>
{
    "de": {
        "Item": "Artikel",
        "Properties": "Attribute",
        "Price": "Preis",
        "Quantity": "Menge",
        "Total": "Gesamt",
        "Tax rate": "Steuersatz",
        "Regular": "Regulär",
        "Reduced": "Vermindert",
        "Net amount to be credited": "Netto Gutschrift-Betrag",
        "Gross amount to be credited": "Brutto Gutschrift-Betrag",
        "Could not find anything": "Es konnte nichts gefunden werden",
        "Comment": "Kommentar",
        "Type": "Typ",
        "Cancellation": "Stornierung",
        "Return": "Widerruf",
        "Status": "Status",
        "Pending": "Ausstehend",
        "Approved": "Genehmigt",
        "Reason": "Rücksendegrund",
        "Comment (optional)": "Kommentar (optional)",
        "No reasons configured yet": "Noch keine Rücksendegründe angelegt",
        "Configure reasons": "Gründe konfigurieren",
        "Excluded from returns per § 312g BGB": "Vom Widerruf ausgeschlossen (§ 312g BGB)",
        "The refund amount will be confirmed when you approve the return.": "Der Erstattungsbetrag wird beim Genehmigen des Widerrufs festgelegt."
    }
}
</i18n>

<script>
import { currencySymbol } from '@/filters'
import pinia from '@/pinia';
import { useSetupStore, useShopPurchaseStore } from '@/stores'

import PropertyValue from '@/extensions/shop/views/Purchase/Form/Partials/PropertyValue'
import ItemMediaPreview from '@/extensions/shop/pointOfSale/views/Partials/ItemMediaPreview'

import PurchasedItemApi from '@/extensions/shop/store/api/purchasedItem';
import returnReasonApi from '@/extensions/shop/store/api/returnReason';

export default {
    name: 'PartiallyCancelForm',
    props: {
        purchase: {
            type: Object,
            required: true,
        },
        form: {
            type: Object,
            required: true,
        },
        initializing: {
            type: Boolean,
            default: false,
        },
        visibility: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        loading: false,
        // credit: {
        //     credited_amount_net: 0,
        //     credited_amount: 0,
        //     comment: '',
        // },
        taxRate: undefined,
        returnReasons: [],
        pagination: {
            filters: {},
            purchase: undefined,
            includeStocks: false,
            includeImages: true,
            includePointOfSalePurchases: true,
            includeWebShopPurchases: true,
            includeInternals: true,
            pageInfo: {
                total: 0,
                perPage: 12,
                currentPage: 1,
                lastPage: 0,
            },
            items: []
        },
    }),
    components: {
        PropertyValue,
        ItemMediaPreview,
    },
    watch: {
        'options.tax.loading': {
            handler(value) {
                if (!value && !this.taxRate) {
                    this.taxRate = this.taxOptions.find(el => el.id === 'regular')
                        || this.taxOptions.find(el => el.id === 'reduced')
                        || this.taxOptions[0]
                }
            },
            immediate: true,
        },
        taxRate: {
            handler(taxRate) {
                const value = this.form.credited_amount || 0
                this.$set(this.form, 'credited_amount_net', _.round(value / (1 + taxRate.value / 100), 2))
            },
            immediate: true,
        },
        // Switching the row type resets `status` to a sane default for that
        // type. Cancellation only ever has status='approved' at creation
        // time (legacy single-click "irrevocable cancel" semantics).
        // Return defaults to 'pending' — the storefront-style flow.
        'form.type': {
            handler(type) {
                if (type === 'cancellation') {
                    this.$set(this.form, 'status', 'approved')
                } else if (type === 'return' && !['pending', 'approved'].includes(this.form.status)) {
                    this.$set(this.form, 'status', 'pending')
                }
            },
            immediate: true,
        },
    },
    computed: {
        creditedAmountNet: {
            get() {
                return this.form.credited_amount_net || undefined
            },
            set(value) {
                this.$set(this.form, 'credited_amount_net', parseFloat(value))

                if (this.form.items.length) {
                    let gross = 0, net = 0
                    this.pagination.items.forEach(item => {
                        if (item.selected && item.take_back_qty) {
                            const price = item.sale_price || item.price
                            const taxRate = item.tax
                            gross += item.take_back_qty * price
                            net += item.take_back_qty * price / (1 + taxRate / 100)
                        }
                    })

                    const taxRate = _.round(gross > 0 ? (gross - net) / net * 100 : 0, 6)
                    this.$set(this.form, 'credited_amount', _.round(value * (1 + taxRate / 100), 2))
                } else if (this.taxRate) {
                    this.$set(this.form, 'credited_amount', _.round(value * (1 + this.taxRate.value / 100), 2))
                }
            }
        },
        creditedAmount: {
            get() {
                return this.form.credited_amount || undefined
            },
            set(value) {
                this.$set(this.form, 'credited_amount', parseFloat(value))

                if (this.form.items.length) {
                    let gross = 0, net = 0
                    this.pagination.items.forEach(item => {
                        if (item.selected && item.take_back_qty) {
                            const price = item.sale_price || item.price
                            const taxRate = item.tax
                            gross += item.take_back_qty * price
                            net += item.take_back_qty * price / (1 + taxRate / 100)
                        }
                    })

                    const taxRate = _.round(gross > 0 ? (gross - net) / net * 100 : 0, 6)
                    this.$set(this.form, 'credited_amount_net', _.round(value / (1 + taxRate / 100), 2))
                } else if (this.taxRate) {
                    this.$set(this.form, 'credited_amount_net', _.round(value / (1 + this.taxRate.value / 100), 2))
                }
            }
        },
        taxOptions() {
            let options = []

            const regularTax = this.options.tax.items.find(el => el.default === true && el.reduced === false)
            if (regularTax) {
                options.push({
                    id: 'regular',
                    reduced: false,
                    value: regularTax.value,
                    label: this.$t('Regular')
                })
            }

            const reducedTax = this.options.tax.items.find(el => el.default === true && el.reduced === true)
            if (reducedTax) {
                options.push({
                    id: 'reduced',
                    reduced: true,
                    value: reducedTax.value,
                    label: this.$t('Reduced')
                })
            }

            const percentages = this.options.tax.items.filter(el => el.default !== true)
            return [...options, ...percentages]
        },
        nothingSelected() {
            let nothing = true
            if (this.pagination.items.length) {
                nothing = this.pagination.items.filter(el => el.selected === true).length === 0
            }
            return nothing
        },
        // A pending return is provisional — the final refund amount is
        // captured by the approval drawer (`ApproveRefundDrawer.vue`), so
        // we suppress the amount inputs here to avoid asking the admin twice.
        isPendingReturn() {
            return this.form.type === 'return' && this.form.status === 'pending';
        },
        customer() {
            return _.get(this.purchase, 'purchaser.customer')
        },
        currency() {
            return currencySymbol(this.purchase.currency)
        },
        setupStore() {
            return useSetupStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        activeCmsId() {
            return _.get(this.activeCms, 'id') || '';
        },
        shopPurchaseStore() {
            return useShopPurchaseStore(pinia);
        },
        options() {
            return this.shopPurchaseStore.options;
        },
    },
    methods: {
        // § 312g BGB: items belonging to a non-returnable product cannot be
        // selected when the admin is recording a Widerruf (type=return).
        // Cancellations (type=cancellation) are not constrained — those are
        // pre-shipment admin decisions, not customer-initiated returns.
        isExcluded(item) {
            if (_.get(this.form, 'type') !== 'return') return false;
            return _.get(item, 'product.returnable') === false;
        },
        toggleItem(item, checked = false) {
            // Defensive: never accept selection on excluded items even if
            // someone bypasses the v-if (e.g. via Vue devtools).
            if (checked && this.isExcluded(item)) return;
            if (checked) {
                this.$set(item, 'selected', true)
                this.$set(item, 'take_back_qty', 1)
            } else {
                this.$delete(item, 'selected')
                this.$delete(item, 'take_back_qty')
            }
            this.updateCreditAmount()
            this.updateItems()
        },
        updateItemTakeBackQty(item, qty) {
            if (qty >= 1) {
                this.$set(item, 'take_back_qty', qty)
            } else {
                this.toggleItem(item)
            }
            this.updateCreditAmount()
            this.updateItems()
        },
        updateCreditAmount() {
            let amount = 0, netAmount = 0
            this.pagination.items.forEach(item => {
                if (! item.selected || ! item.take_back_qty) return
                const price = item.sale_price || item.price
                const taxRate = item.tax

                amount += item.take_back_qty * price
                netAmount += item.take_back_qty * price / (1 + taxRate / 100)
            })

            const roundedNet = _.round(netAmount, 2)
            const roundedGross = _.round(amount, 2)

            this.$set(this.form, 'credited_amount_net', roundedNet)
            this.$set(this.form, 'credited_amount', roundedGross)

            // The validation cap equals the pre-filled value: the admin can
            // lower the credited amount but never exceed the selected items'
            // worth. Read by Form.vue → partialCancelPurchaseRules.
            this.$set(this.form, 'max_credited_amount_net', roundedNet)
            this.$set(this.form, 'max_credited_amount', roundedGross)
        },
        updateItems() {
            this.form.items = []

            this.pagination.items.forEach(item => {
                if (! item.selected || ! item.take_back_qty) return
                const pivot = {
                    qty: item.take_back_qty,
                }
                if (item.return_reason_id) {
                    pivot.reason = { id: parseInt(item.return_reason_id) }
                }
                if (item.return_comment) {
                    pivot.comment = item.return_comment
                }
                this.form.items.push({
                    id: item.id,
                    pivot,
                })
            })
        },
        async loadPurchasedItems(page = 1) {
            this.$set(this.pagination.pageInfo, 'currentPage', page)
            this.loading = true;

            if (!this.pagination.filters) {
                this.$set(this.pagination, 'filters', {})
            }
            this.$set(this.pagination.filters, 'shop', {
                key: 'shop',
                match: this.activeCms.shop.id
            })
            // this.$set(this.pagination.filters, 'purchase_id', {
            //     key: 'purchase_id',
            //     match: this.purchase.id
            // })

            if (this.customer) {
                this.$set(this.pagination.filters, 'customer', {
                    key: 'customer',
                    match: this.customer.id
                })
            }

            const paginatedPurchasedItems = await this.fetchPurchasedItemsPaginated(this.pagination)
                .catch(err => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });

                    console.error(err)
                })

            if (paginatedPurchasedItems.items && paginatedPurchasedItems.items.length) {
                if (page > 1) {
                    const items = _.cloneDeep(paginatedPurchasedItems.items)
                    this.pagination.items = [...this.pagination.items, ...items]
                } else {
                    this.pagination = _.cloneDeep(paginatedPurchasedItems);
                    // this.$emit('update-visibility', true)
                    this.$emit('update:initializing', false)
                    // this.updateAutofocusSearchInput(false)
                    // this.$emit('blur-search')

                    this.$nextTick(() => {
                        if (this.$refs.container) {
                            // this.$refs.container.style['scroll-behavior'] = 'smooth';
                            this.$refs.container.scrollTop = 0
                        }
                        this.loadMoreIfNeeded(this.$refs.container)
                        // console.log(this.$refs.container)
                    })
                }
            } else {
                setTimeout(() => {
                    this.$emit('update:visibility', false)

                    this.$notify.error({
                        title: this.$t('Could not find anything'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                }, 600)
            }

            this.loading = false;
        },
        handleScroll(event) {
            const { target } = event;
            this.loadMoreIfNeeded(target)
        },
        loadMoreIfNeeded(target) {
            const currentPage = this.pagination.pageInfo.currentPage;
            if (this.loading === false && this.pagination.pageInfo.lastPage > currentPage) {

                let loadMore = false;
                const offset = 40;

                if (Math.ceil(target.scrollTop) >= target.scrollHeight - target.offsetHeight - offset) {
                    loadMore = true;
                }

                if (loadMore) {
                    this.loadPurchasedItems(currentPage + 1);
                }
            }
        },
        async fetchPurchasedItemsPaginated(payload) {
            let mimicCustomer = this.customer ? { id: this.customer.id } : null

            const customerGroup = _.get(this.customer, 'customerGroup')
                || _.get(this.activeCms, 'shop.defaultCustomerGroup')

            const address = _.get(this.purchase, 'purchaser.shippingAddress')
                || _.get(this.purchase, 'purchaser.billingAddress')

            if (!mimicCustomer && customerGroup) {
                mimicCustomer = {
                    id: null,
                    customerGroup,
                    member: { address },
                }
            }

            return PurchasedItemApi.fetchPurchasedItemsPaginated({ ...payload, mimicCustomer }, this.purchase.id);
        }
    },
    mounted() {
        this.$emit('update:initializing', true)
        this.loadPurchasedItems()

        const shopId = _.get(useSetupStore().activeCms, 'shop.id')
        if (shopId) {
            returnReasonApi.fetchReturnReasons(shopId)
                .then(({ data }) => { this.returnReasons = (data || []).filter(r => r.active) })
                .catch(err => console.error(err))
        }

        if (!this.taxRate) {
            this.taxRate = this.taxOptions.find(el => el.id === 'regular')
                || this.taxOptions.find(el => el.id === 'reduced')
                || this.taxOptions[0]
        }
    },
}
</script>

<style lang="sass" scoped>
.drawer-spinner-container
    padding-bottom: 1rem

// § 312g BGB exclusion — visually dim the row and the ban icon so the
// admin sees at a glance which items can't be added to a Widerruf.
tr.item--excluded
    opacity: 0.5
    .name b
        text-decoration: line-through
        text-decoration-color: rgba(0, 0, 0, 0.4)

.excluded-icon
    color: $--color-warning

// Pending-return placeholder where the amount inputs used to sit.
.pending-amount-hint
    margin: 12px 0
    padding: 10px 14px
    background: rgba(160, 174, 192, 0.12)
    border-left: 3px solid #a0aec0
    color: var(--color-text-secondary, #606266)
    font-size: 13px
    line-height: 1.4
    i
        margin-right: 8px
        color: #a0aec0

.partially-cancel-form
    // height: 100%
    height: calc(100vh - 66px - 72px)
    padding-left: 20px
    padding-right: 20px
    overflow-y: auto

.partial-cancel-context
    display: flex
    flex-wrap: wrap
    gap: 24px
    padding: 12px 0 0
    margin-bottom: 8px
    border-bottom: 1px solid var(--border-color, #ebeef5)
    .context-field
        margin-bottom: 12px
        &::v-deep
            .el-form-item__label
                padding-bottom: 4px
                font-size: 12px
                font-weight: 600
                color: var(--color-text-secondary, #606266)
                line-height: 1.2

// Sub-row beneath a selected return item — carries reason+comment
// without crowding the main item cell. Subtle background ties it
// visually to the row above.
tr.return-meta-row
    > td
        padding: 0 12px 12px
        background: rgba(0, 0, 0, 0.02)
        border-top: 0
    .return-meta
        display: flex
        align-items: center
        gap: 8px
        flex-wrap: wrap
        .reason
            width: 220px
            flex-shrink: 0
        .comment
            flex: 1
            min-width: 200px
        .configure-hint
            font-size: 12px
            color: var(--color-text-muted, #909399)
            white-space: nowrap
            text-decoration: underline
            &:hover
                color: var(--color-primary, #409eff)

label.el-checkbox
    &::v-deep
        .el-checkbox__input
            .el-checkbox__inner
                width: 18px
                height: 18px
                &:after
                    scale: 1.6
                    left: 6px
                    top: 2px
            &:not(.is-checked)
                .el-checkbox__inner
                    border-color: lighten($--shop-color-primary, 30%)
        &:hover,
        &:focus
            .el-checkbox__input:not(.is-checked)
                .el-checkbox__inner
                    border-color: $--shop-color-primary
.purchased-items
    // max-height: 50vh
    // max-height: calc(100vh - 66px - 76px)
    // overflow-y: auto
    // position: relative
    margin-bottom: 1rem
    thead th
        position: sticky
        top: 0

th.status,
td.status
    width: 1px
    color: $--color-danger
    font-size: 1.2rem

body table.default-table.default-table
    margin-top: 0
</style>
